import { memo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { cn, imageProps, lqipStyle } from '../../lib/format.js';
import { SpecStrip } from './SpecStrip.jsx';
import { PriceTag } from './PriceTag.jsx';
import { Rating } from './Rating.jsx';
import { Badge } from '../ui/Badge.jsx';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * The product card.
 *
 * Two behaviours on hover (PRD §8): the image crossfades to the second shot, and
 * the spec strip slides up. Both are decoration — the strip is always in the DOM
 * and always readable, it simply sits below the fold of the card until hovered
 * on a pointer device. On touch it is visible from the start, because there is
 * no hover to reveal it with.
 *
 * The image is a plain <img> with srcset, explicit dimensions, and lazy loading.
 * Wrapping catalogue imagery in a fetch-to-blob would forfeit HTTP caching,
 * streaming decode, and priority hints, and would make the store measurably
 * slower.
 */

const ProductCardComponent = ({ product, priority = false, className, onQuickAdd }) => {
  const [hovered, setHovered] = useState(false);
  const reduced = useReducedMotion();
  const imageRef = useRef(null);

  const primary = product.images?.[0];
  const secondary = product.images?.[1];

  const isVariable = product.type === 'variable';
  const price = isVariable ? (product.priceRange?.min ?? 0) : product.price;
  const compareAt = isVariable ? 0 : product.compareAtPrice;

  const soldOut = product.trackInventory !== false && (isVariable ? product.totalStock : product.stock) <= 0;

  return (
    <article
      className={cn('group relative flex flex-col', className)}
      data-world={product.world}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link
        to={`/product/${product.slug}`}
        className="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent"
        // The whole card is one link; everything inside it is presentational.
        aria-label={`${product.title}, ${product.brand?.name ?? ''}`}
      >
        <div
          className="relative aspect-4/5 w-full overflow-hidden bg-mist/40"
          style={lqipStyle(primary)}
          ref={imageRef}
        >
          {primary && (
            <img
              {...imageProps(primary, { sizes: '(min-width: 1280px) 22vw, (min-width: 768px) 33vw, 50vw', eager: priority })}
              alt={primary.alt || product.title}
              className={cn(
                'absolute inset-0 size-full object-cover transition-opacity duration-500',
                secondary && hovered && !reduced ? 'opacity-0' : 'opacity-100',
              )}
            />
          )}

          {secondary && (
            <img
              {...imageProps(secondary, { sizes: '(min-width: 1280px) 22vw, (min-width: 768px) 33vw, 50vw' })}
              alt=""
              aria-hidden="true"
              className={cn(
                'absolute inset-0 size-full object-cover transition-opacity duration-500',
                hovered && !reduced ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}

          <div className="pointer-events-none absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {soldOut && <Badge tone="solid">Sold out</Badge>}
            {!soldOut && product.isNewArrival && <Badge tone="accent">New</Badge>}
            {!soldOut && !product.isNewArrival && compareAt > price && <Badge tone="success">Reduced</Badge>}
          </div>

          {onQuickAdd && !soldOut && (
            <div
              className={cn(
                'absolute inset-x-3 bottom-3 transition-all duration-300',
                reduced
                  ? 'opacity-100'
                  : 'translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100',
              )}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  onQuickAdd(product, imageRef.current);
                }}
                className="w-full rounded bg-ink/92 py-2.5 text-2xs font-medium tracking-[0.06em] text-white uppercase backdrop-blur-sm transition-colors hover:bg-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {isVariable ? 'Choose an option' : 'Add to bag'}
              </button>
            </div>
          )}
        </div>

        <div className="pt-3">
          {/* The rail. Present in the DOM at all times; revealed on hover where
              there is a pointer to hover with. */}
          <div
            className={cn(
              'overflow-hidden transition-all duration-300',
              reduced
                ? 'max-h-8 opacity-100'
                : 'max-h-8 opacity-100 sm:max-h-0 sm:opacity-0 sm:group-hover:max-h-8 sm:group-hover:opacity-100 sm:group-focus-within:max-h-8 sm:group-focus-within:opacity-100',
            )}
          >
            <SpecStrip items={product.specStrip} className="pb-1.5" />
          </div>

          {product.brand?.name && (
            <p className="text-2xs tracking-[0.08em] text-muted uppercase">{product.brand.name}</p>
          )}

          <h3 className="mt-1 text-sm leading-snug text-ink transition-colors group-hover:text-accent">
            {product.title}
          </h3>
        </div>
      </Link>

      <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <PriceTag
          price={price}
          compareAtPrice={compareAt}
          priceRange={product.priceRange}
          isVariable={isVariable}
          size="sm"
        />
        {product.ratingCount > 0 && <Rating value={product.ratingAvg} count={product.ratingCount} />}
      </div>
    </article>
  );
};

/**
 * A shop page renders up to a hundred of these and re-renders on every filter
 * change. Memoising on the product id keeps that to the cards that changed.
 */
export const ProductCard = memo(
  ProductCardComponent,
  (previous, next) =>
    previous.product._id === next.product._id &&
    previous.product.totalStock === next.product.totalStock &&
    previous.priority === next.priority,
);

export default ProductCard;
